import 'photoswipe/style.css';

import { photoswipe } from '@/storybook/theme';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import { onUnmounted } from 'vue';

const ZOOM_STEP_FACTOR = 1.5;
const ZOOM_BOUND_EPSILON = 0.001;

const ZOOM_IN_SVG = `
  <svg aria-hidden="true" class="pswp__icn" viewBox="0 0 20 20" width="20" height="20">
    <path d="M10 4v12M4 10h12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
  </svg>
`;

const ZOOM_OUT_SVG = `
  <svg aria-hidden="true" class="pswp__icn" viewBox="0 0 20 20" width="20" height="20">
    <path d="M4 10h12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
  </svg>
`;

const arrangeLightboxControls = () => {
  const lightboxRoot = document.querySelector<HTMLElement>('.pswp');
  const closeButton = lightboxRoot?.querySelector<HTMLElement>('.pswp__button--close');
  const zoomInButton = lightboxRoot?.querySelector<HTMLElement>('.pswp__button--zoom-in');
  const zoomOutButton = lightboxRoot?.querySelector<HTMLElement>('.pswp__button--zoom-out');

  if (!lightboxRoot || !closeButton || !zoomInButton || !zoomOutButton) {
    return;
  }

  let controlStack = lightboxRoot.querySelector<HTMLElement>('.pswp__control-stack');
  if (!controlStack) {
    controlStack = document.createElement('div');
    controlStack.className = 'pswp__control-stack';
    lightboxRoot.append(controlStack);
  }

  let zoomGroup = controlStack.querySelector<HTMLElement>('.pswp__zoom-controls');
  if (!zoomGroup) {
    zoomGroup = document.createElement('div');
    zoomGroup.className = 'pswp__zoom-controls';
  }

  if (closeButton.parentElement !== controlStack) {
    controlStack.append(closeButton);
  }
  if (zoomGroup.parentElement !== controlStack) {
    controlStack.append(zoomGroup);
  }
  if (zoomInButton.parentElement !== zoomGroup) {
    zoomGroup.append(zoomInButton);
  }
  if (zoomOutButton.parentElement !== zoomGroup) {
    zoomGroup.append(zoomOutButton);
  }
};

export const usePhotoSwipe = (src: string) => {
  let lightbox: PhotoSwipeLightbox | null = null;
  let controlObserver: MutationObserver | null = null;
  let controlRafId: number | undefined;
  let controlTimeoutIds: number[] = [];
  let zoomInButton: HTMLButtonElement | null = null;
  let zoomOutButton: HTMLButtonElement | null = null;
  let pendingBoundaryZoomLevel: number | null = null;

  const updateZoomControlState = (zoomLevel?: number) => {
    const slide = lightbox?.pswp?.currSlide;
    if (!slide) {
      return;
    }

    let currentZoomLevel = zoomLevel ?? slide.currZoomLevel;

    if (pendingBoundaryZoomLevel !== null) {
      if (lightbox?.pswp?.animations.isPanRunning()) {
        currentZoomLevel = pendingBoundaryZoomLevel;
      } else {
        pendingBoundaryZoomLevel = null;
      }
    }

    if (zoomInButton) {
      zoomInButton.disabled = currentZoomLevel >= slide.zoomLevels.max - ZOOM_BOUND_EPSILON;
    }
    if (zoomOutButton) {
      zoomOutButton.disabled = currentZoomLevel <= slide.zoomLevels.initial + ZOOM_BOUND_EPSILON;
    }
  };

  const zoomByStep = (direction: 'in' | 'out') => {
    const pswp = lightbox?.pswp;
    const slide = pswp?.currSlide;
    if (!pswp || !slide) {
      return;
    }

    const nextZoomLevel =
      direction === 'in'
        ? Math.min(slide.currZoomLevel * ZOOM_STEP_FACTOR, slide.zoomLevels.max)
        : Math.max(slide.currZoomLevel / ZOOM_STEP_FACTOR, slide.zoomLevels.initial);

    slide.zoomTo(nextZoomLevel, undefined, pswp.options.zoomAnimationDuration);
    pendingBoundaryZoomLevel = nextZoomLevel === slide.zoomLevels.initial || nextZoomLevel === slide.zoomLevels.max ? nextZoomLevel : null;
    updateZoomControlState(nextZoomLevel);
  };

  const registerZoomControls = () => {
    const pswp = lightbox?.pswp;
    if (!pswp) {
      return;
    }

    pswp.ui.registerElement({
      name: 'zoomOut',
      className: 'pswp__button--zoom-out',
      html: ZOOM_OUT_SVG,
      isButton: true,
      title: 'Zoom out',
      order: 22,
      onInit: element => {
        zoomOutButton = element as HTMLButtonElement;
        zoomOutButton.setAttribute('data-testid', 'pswp-zoom-out');
        zoomOutButton.disabled = true;
      },
      onClick: () => zoomByStep('out'),
    });

    pswp.ui.registerElement({
      name: 'zoomIn',
      className: 'pswp__button--zoom-in',
      html: ZOOM_IN_SVG,
      isButton: true,
      title: 'Zoom in',
      order: 21,
      onInit: element => {
        zoomInButton = element as HTMLButtonElement;
        zoomInButton.setAttribute('data-testid', 'pswp-zoom-in');
      },
      onClick: () => zoomByStep('in'),
    });
  };

  const requestLightboxControlPositioning = () => {
    if (controlRafId !== undefined) {
      return;
    }

    controlRafId = requestAnimationFrame(() => {
      controlRafId = undefined;
      arrangeLightboxControls();
    });
  };

  const stopLightboxControlPositioning = () => {
    controlObserver?.disconnect();
    controlObserver = null;

    if (controlRafId !== undefined) {
      cancelAnimationFrame(controlRafId);
      controlRafId = undefined;
    }

    controlTimeoutIds.forEach(timeoutId => window.clearTimeout(timeoutId));
    controlTimeoutIds = [];
  };

  const scheduleLightboxControlPositioning = () => {
    arrangeLightboxControls();
    requestLightboxControlPositioning();

    controlTimeoutIds.forEach(timeoutId => window.clearTimeout(timeoutId));
    controlTimeoutIds = [50, 150, 300, 600, 1000, 2000].map(delay => window.setTimeout(arrangeLightboxControls, delay));

    if (!controlObserver && document.body) {
      controlObserver = new MutationObserver(requestLightboxControlPositioning);
      controlObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class', 'style'],
        childList: true,
        subtree: true,
      });
    }
  };

  const init = (width?: number, height?: number) => {
    lightbox = new PhotoSwipeLightbox({
      dataSource: [
        {
          width,
          height,
          src,
        },
      ],
      arrowKeys: false,
      initialZoomLevel: 'fit',
      zoom: false,
      tapAction: 'zoom',
      closeSVG: photoswipe.closeSVGString,
      counter: false,
      preloadFirstSlide: true,
      bgOpacity: 0.8,
      pswpModule: () => import('photoswipe'),
    });

    lightbox.on('uiRegister', registerZoomControls);
    lightbox.on('zoomPanUpdate', () => updateZoomControlState());
    lightbox.on('afterInit', scheduleLightboxControlPositioning);
    lightbox.on('afterInit', () => updateZoomControlState());
    lightbox.on('bindEvents', scheduleLightboxControlPositioning);
    lightbox.on('openingAnimationStart', scheduleLightboxControlPositioning);
    lightbox.on('openingAnimationEnd', scheduleLightboxControlPositioning);
    lightbox.on('resize', scheduleLightboxControlPositioning);
    lightbox.on('close', stopLightboxControlPositioning);
    lightbox.on('destroy', stopLightboxControlPositioning);
    lightbox?.init();
  };

  const open = () => {
    lightbox?.loadAndOpen(0);
    scheduleLightboxControlPositioning();
  };

  onUnmounted(() => {
    // PhotoSwipeLightbox#destroy only forwards to an open pswp instance, so a lightbox
    // that was never opened emits no `destroy` event. Tear the timers down directly or
    // the pending positioning timeouts outlive the component.
    stopLightboxControlPositioning();

    if (lightbox) {
      lightbox.destroy();
      lightbox = null;
    }
  });

  return { lightbox, open, init };
};
